import dayjs from 'dayjs';
import type {
  ExpeditionDetailV3OutDTO,
  GetExternalOrdersResponse,
  GlobalOrdersRequest,
  LocationPreviewForCheckExpResponseOutDTO,
  OrderReservationInDTO,
  PriceRequest,
  ReservationDataInDTO,
} from '@api/ringil-api.v2.tsx';
import { emptyOrder, isExternalIdPresent, Order } from '@components/framework/orders/common.tsx';
import { toEditableFlags } from '@components/RingilComponents/form/flagUnitsLimit.ts';
import type { CreateTimeslotForm, Timeslot } from '@components/ringil3/Features/Reservation/Sidedbars/types.ts';
import { apiPragueFromDayjsLocal, apiPragueFromDayjsUtc, apiUtcFromDayjs, dayjsFromDayAndTimeStrings } from '@utils/dateWithTz.tsx';
import { isNotBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';

/**
 * RD-3062 — the form ↔ API mapping of `CreateTimeslotSidebar`, moved out of the component unchanged so it
 * can be unit-tested. The component keeps the hooks, API calls and UI; everything here is pure.
 */

type Translate = (key: string, fallback: string) => string;

export interface CreateTimeslotInitialDataArgs {
  companyLocationId: string;
  companyLocationCustomerId: string;
  preferredDate?: dayjs.Dayjs;
  cameraId?: string;
  carPlate?: string;
  rampId?: string;
  arrivalHourString?: string;
  timezoneId?: string;
}

/** The empty form a new reservation starts from. */
export const createTimeslotInitialData = (
  { companyLocationId, companyLocationCustomerId, preferredDate, cameraId, carPlate, rampId, arrivalHourString, timezoneId }: CreateTimeslotInitialDataArgs,
  showOrders: boolean,
  requiredOrderItems: boolean,
): CreateTimeslotForm => {
  return {
    actionTypes: [],
    reservationName: '',
    reservationNote: null,
    companyLocationId,
    customerCompanyCode: null,
    companyLocationCustomerId,
    orders: showOrders === true ? [emptyOrder(requiredOrderItems)] : null,
    timeslot: {
      rampId: rampId ?? null,
      durationInMin: '30',
      arrivalHourString: arrivalHourString ?? null,
      localDate: isNotNullOrUndefined(preferredDate) ? preferredDate.format('YYYY-MM-DD') : dayjs().utc().format('YYYY-MM-DD'),
      timezoneId: timezoneId,
    },
    driverInfo: {
      driverName: null,
      driverId: null,
      driverPhone: null,
    },
    carPlate: carPlate,
    carrierId: null,
    cameraId: cameraId,
    openGate: isNotBlank(cameraId),
    createExpedition: false,
    truckType: null,
    flags: [],
    onBehalfOfCodeListId: null,
    sendNotification: true,
    folders: [],
  };
};

export interface ExpeditionOrdersForReservation {
  /** One order per name, in the order first seen — what the form is prefilled with. */
  orders: Order[];
  /** Orders found on the expedition before de-duplication; zero means the expedition carries none. */
  sourceCount: number;
}

/**
 * The orders of a linked expedition, as the reservation form shows them: those of the reservation's own
 * stop when it is known (else of every stop), loaded and unloaded alike, one per order name.
 */
export const expeditionOrdersForReservation = (d: ExpeditionDetailV3OutDTO, expeditionLocationId?: string): ExpeditionOrdersForReservation => {
  let ordersToProcess: any[] = [];

  if (expeditionLocationId) {
    const expeditionLocationIdToMatch = expeditionLocationId;
    const targetLocationDetails = d.locations.find(loc => loc.id === expeditionLocationIdToMatch);

    if (targetLocationDetails) {
      const loaded = targetLocationDetails.loadedOrders || [];
      const unloaded = targetLocationDetails.unloadedOrders || [];
      ordersToProcess = loaded.concat(unloaded);
    } else {
      d.locations.forEach(location => {
        ordersToProcess = ordersToProcess.concat(location.loadedOrders || [], location.unloadedOrders || []);
      });
    }
  } else {
    d.locations.forEach(location => {
      ordersToProcess = ordersToProcess.concat(location.loadedOrders || [], location.unloadedOrders || []);
    });
  }

  const mappedOrders = ordersToProcess.map(o => ({
    orderName: o.orderName,
    items: (o.items || []).map((item: any) => ({
      amount: item.amount,
      cargoTemplateCode: item.cargoTemplate?.code || item.cargoItemTypeCode,
      stackable: item.stackable,
      itemNote: null as string | null,
      decimalPlaces: item.cargoTemplate?.decimalPlaces === undefined ? 0 : item.cargoTemplate.decimalPlaces,
      height: item.height || (null as number | null),
      width: item.width || (null as number | null),
      length: item.length || (null as number | null),
      weight: item.weight || (null as number | null),
    })),
    orderNote: o.orderNote || (null as string | null),
    orderValue: o.orderValue || (null as PriceRequest | null),
    orderAmount: o.orderAmount || (null as number | null),
    orderDescription: o.orderDescription || (null as string | null),
  }));

  const uniqueOrdersByName = new Map<string, Order>();
  mappedOrders.forEach(order => {
    if (order.orderName && !uniqueOrdersByName.has(order.orderName)) {
      uniqueOrdersByName.set(order.orderName, order);
    }
  });

  return { orders: Array.from(uniqueOrdersByName.values()), sourceCount: ordersToProcess.length };
};

/** Orders as the slot check and free-slot search read them: only orders with at least one complete item. */
export const mapGlobalOrderWithItemsForCheckTimeslots = (orders: Order[]) => {
  return orders
    .map(order => {
      // I need to have at least one item with amount and cargoTemplateCode
      const filteredItems = order.items.filter(item => item.amount !== null && item.cargoTemplateCode !== null);

      if (filteredItems.length > 0) {
        return {
          orderNote: order.orderNote,
          orderName: order.orderName,
          orderValue: order.orderValue,
          cargoAmount: order.orderAmount,
          cargoDescription: order.orderDescription,
          items: filteredItems.map(item => ({
            amount: item.amount,
            cargoTemplateCode: item.cargoTemplateCode,
            note: item.itemNote,
            stackable: item.stackable,
          })),
        };
      }

      return null;
    })
    .filter(order => order !== null);
};

/**
 * The timeslot the live availability check sends while the user is still picking (legacy Prague + UTC).
 * `durationInMin` is the form's string; dayjs `add` has always converted it itself, hence the cast.
 */
export const timeslotForReservationCheck = (value: Timeslot) => ({
  since: apiPragueFromDayjsUtc(dayjsFromDayAndTimeStrings(value.localDate, value.arrivalHourString, value.timezoneId), 'Europe/Prague'),
  till: apiPragueFromDayjsUtc(dayjsFromDayAndTimeStrings(value.localDate, value.arrivalHourString, value.timezoneId).add(value.durationInMin as unknown as number, 'minute'), 'Europe/Prague'),
  utcStart: apiUtcFromDayjs(dayjsFromDayAndTimeStrings(value.localDate, value.arrivalHourString, value.timezoneId)),
  utcEnd: apiUtcFromDayjs(dayjsFromDayAndTimeStrings(value.localDate, value.arrivalHourString, value.timezoneId).add(value.durationInMin as unknown as number, 'minute')),
  timezoneId: value.timezoneId,
});

/** The timeslot a saved reservation sends; undefined while the slot is not fully picked. */
export const computeTimeslot = (timeslot: Timeslot) => {
  const sinceLocal = dayjsFromDayAndTimeStrings(timeslot.localDate, timeslot.arrivalHourString, 'Europe/Prague');
  const sinceTz = dayjsFromDayAndTimeStrings(timeslot.localDate, timeslot.arrivalHourString, timeslot.timezoneId);
  // localDate/arrivalHourString/timezoneId can be missing when the form is submitted before the slot
  // is fully picked; dayjsFromDayAndTimeStrings then returns null and .add() below would crash. Bail
  // (the backend rejects a ramp reservation without a slot via onValidationFailed). (Sentry QM.)
  if (isNullOrUndefined(sinceLocal) || isNullOrUndefined(sinceTz)) return undefined;
  const durationMin = parseInt(timeslot.durationInMin);
  return {
    since: apiPragueFromDayjsLocal(sinceLocal),
    till: apiPragueFromDayjsLocal(sinceLocal.add(durationMin, 'minute')),
    utcStart: apiUtcFromDayjs(sinceTz),
    utcEnd: apiUtcFromDayjs(sinceTz.add(durationMin, 'minute')),
    timezoneId: timeslot.timezoneId,
  };
};

/** What the sidebar was opened with that the request carries over (see `CreateTimeslotRequest`). */
export interface CreateTimeslotContext {
  cameraLogId?: string;
  expeditionLocationId?: string;
  reservationId?: string;
}

export interface CreateReservationRequestArgs {
  /** The validated form data handed to the submit handler. */
  data: CreateTimeslotForm;
  /** The live form values — a few fields have always been read from here rather than from `data`. */
  values: CreateTimeslotForm;
  context: CreateTimeslotContext;
  isReservationOrderLinkedToExpOrder: boolean;
  externalOrdersResponse?: GetExternalOrdersResponse;
  onBehalfOfEnabled: boolean;
}

/** The body of `createReservation`. */
export const buildCreateReservationRequest = ({
  data,
  values,
  context,
  isReservationOrderLinkedToExpOrder,
  externalOrdersResponse,
  onBehalfOfEnabled,
}: CreateReservationRequestArgs): ReservationDataInDTO => ({
  openGate: values.openGate,
  cameraId: values.cameraId,
  cameraLogId: context.cameraLogId,
  linkToExpeditionLocationId: context.expeditionLocationId,
  rampId: data.timeslot.rampId,
  labelIds: [],
  folderIds: (data.folders ?? []).map(f => f.id),
  carPlate: data.carPlate,
  locationId: values.companyLocationId,
  customerId: values.companyLocationCustomerId,
  reservationNote: values.reservationNote,
  reservationName: values.reservationName,
  driverInfo: data.driverInfo,
  carrierNote: data.carrierNote,
  carrierId: data.carrierId,
  reservationActionTypes: data.actionTypes,
  timeslot: data.timeslot.rampId ? computeTimeslot(data.timeslot) : undefined,
  createExpedition: data.createExpedition,
  flags: toEditableFlags(data.flags),
  fromReservationId: data.createExpedition ? context.reservationId : null,
  truckTypeCode: data.truckType,
  sendNotification: values.sendNotification,
  onBehalfOfCodeListId: onBehalfOfEnabled ? data.onBehalfOfCodeListId ?? null : undefined,
  orders: isReservationOrderLinkedToExpOrder
    ? null
    : data.orders.map(
        t =>
          ({
            orderNote: t.orderNote,
            orderName: t.orderName,
            orderValue: t.orderValue,
            cargoAmount: t.orderAmount,
            cargoDescription: t.orderDescription,
            externalOrders: isExternalIdPresent(externalOrdersResponse) ? t.externalOrders : undefined,
            items: t.items.map(t => ({
              amount: t.amount,
              cargoTemplateCode: t.cargoTemplateCode,
              itemNote: t.itemNote,
              stackable: t.stackable,
              height: t.height,
              width: t.width,
              length: t.length,
              weight: t.weight,
            })),
          } as GlobalOrdersRequest),
      ),
  order: (data.orders ?? [])
    .map(
      t =>
        ({
          orderType: null,
          orderNote: t.orderNote,
          reservationName: t.orderName,
        } as OrderReservationInDTO),
    )
    .find((_, index) => index == 0),
});

/** Why a linked expedition cannot be created from the form's orders, or null when it can. */
export const checkOrderMismatch = (formOrders: Order[], expeditionLocations: LocationPreviewForCheckExpResponseOutDTO[], tr: Translate): string | null => {
  const formOrderNames = formOrders?.map(order => order.orderName) || [];
  const expeditionOrderNames = expeditionLocations.flatMap(location => location.orderNames);

  const hasExtraExpeditionOrders = expeditionOrderNames.some(name => !formOrderNames.includes(name));
  const hasNoOrders = formOrderNames.length === 0;
  const hasExtraFormOrders = formOrderNames.some(name => !expeditionOrderNames.includes(name));

  if (hasNoOrders) {
    return tr(`CreateTimeslotSidebar.createExpeditionWarningNoOrders`, `No orders selected in the form`);
  } else if (hasExtraFormOrders) {
    return tr(`CreateTimeslotSidebar.createExpeditionWarningExtraFormOrders`, `Form contains orders not present in the expedition`);
  }

  return null;
};
